import { createContext } from "react";

// ----- CONSTANTS ----- //
export const ENTITY_TYPES = {
  EMPLOYEE: "employee",
  REPRESENTATIVE: "representative",
  VOUCHER_CAMPAIGN: "voucherCampaign",
  CREATE_VOUCHER: "createVoucher",
  EXTERNAL_CONTACT: "externalContact",
  SUPPLIER: "supplier",
  BENEFICIARY: "beneficiary",
} as const;

export type EntityType = typeof ENTITY_TYPES[keyof typeof ENTITY_TYPES];

// ----- DRAFT TYPES ----- //
export type Draft<T = Record<string, unknown>> = {
  id: string | null // null for new entity
  formData: Partial<T>
  originalData: Partial<T>
  changedFields: string[]
  lastModified: number
};

export type DraftContextType = {
  /** Initialize a draft with original values for comparison */
  initializeDraft: <T>(
    entityType: string,
    entityId: string | null,
    originalData: Partial<T>,
  ) => void
  /** Save a complete draft for an entity */
  saveDraft: <T>(
    entityType: string,
    entityId: string | null,
    formData: Partial<T>,
    changedFields: string[],
  ) => void
  /** Update a single field in a draft */
  updateDraftField: (
    entityType: string,
    entityId: string | null,
    fieldName: string,
    fieldValue: unknown,
  ) => void
  /** Remove a draft for an entity */
  removeDraft: (entityType: string, entityId: string | null) => void
  /** Get a draft for an entity */
  getDraft: <T>(entityType: string, entityId: string | null) => Draft<T> | null
  /** Check if a draft exists for an entity */
  hasDraft: (entityType: string, entityId: string | null) => boolean
  /** Get list of changed fields for an entity */
  getChangedFields: (entityType: string, entityId: string | null) => string[]
  /** Clear all drafts for all entities */
  clearAllDrafts: () => void
};

// ----- CONTEXT ----- //
export const DraftContext = createContext<DraftContextType | undefined>(undefined);
